import React, { useState, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import BeneficiaryApiExplorer from '../components/BeneficiaryApiExplorer';
import DateRangePicker from '../components/DateRangePicker';
import { usePartners } from '../context/PartnersContext';
import { useApiMode } from '../context/ApiModeContext';
import './PageStyles.css';
import './Beneficiaries.css';


const getPartnerInitials = (partnerName) => {
  const words = (partnerName || '').trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }
  return (partnerName || '').slice(0, 2).toUpperCase() || 'XX';
};

const buildInitialBeneficiaries = (partners) => {
  const list = [];
  let seqByPartner = {};
  const raw = [
    { accountId: 412312, individualCount: 1, businessCount: 1, createdAt: '2026-02-01', connectionLabel: 'Connection 1' },
    { accountId: 412312, individualCount: 2, businessCount: 0, createdAt: '2026-02-05', connectionLabel: 'Connection 1' },
    { accountId: 412400, individualCount: 1, businessCount: 2, createdAt: '2026-02-08', connectionLabel: 'Connection 2' },
    { accountId: 412500, individualCount: 0, businessCount: 1, createdAt: '2026-02-10', connectionLabel: 'Connection 3' },
  ];
  raw.forEach((r) => {
    const partner = partners.find((p) => p.name === r.connectionLabel) || partners[0];
    const partnerName = partner ? partner.name : r.connectionLabel;
    const initials = getPartnerInitials(partnerName);
    seqByPartner[partnerName] = (seqByPartner[partnerName] || 0) + 1;
    const seq = String(seqByPartner[partnerName]).padStart(6, '0');
    const id = `B${initials}${seq}`;
    list.push({
      id,
      accountId: r.accountId,
      individualCount: r.individualCount,
      businessCount: r.businessCount,
      createdAt: r.createdAt,
      partnerId: partner ? partner.id : null,
      partnerName,
    });
  });
  return list.sort((a, b) => b.id.localeCompare(a.id));
};

const Beneficiaries = () => {
  const { partners, addLogForPartner } = usePartners();
  const { apiBaseUrl } = useApiMode();
  const [beneficiaries] = useState(() => buildInitialBeneficiaries(partners));
  const [filterBeneficiaryId, setFilterBeneficiaryId] = useState('');
  const [filterAccountId, setFilterAccountId] = useState('');
  const [filterPartnerId, setFilterPartnerId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTryApiSidebar, setShowTryApiSidebar] = useState(false);

  const filteredBeneficiaries = useMemo(() => {
    return beneficiaries.filter((b) => {
      if (filterBeneficiaryId && !b.id.toLowerCase().includes(filterBeneficiaryId.trim().toLowerCase())) return false;
      if (filterAccountId && String(b.accountId) !== String(filterAccountId)) return false;
      if (filterPartnerId && String(b.partnerId) !== String(filterPartnerId)) return false;
      if (dateFrom && b.createdAt < dateFrom) return false;
      if (dateTo && b.createdAt > dateTo) return false;
      return true;
    });
  }, [beneficiaries, filterBeneficiaryId, filterAccountId, filterPartnerId, dateFrom, dateTo]);

  const handleDateChange = (start, end) => {
    setDateFrom(start || '');
    setDateTo(end || '');
  };

  const dateRangeLabel = dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : dateFrom ? `${dateFrom} → Select end` : 'Date range';

  return (
    <DashboardLayout>
      <div className="page-container">
        <div className="card">
          <div className="card-header card-header-beneficiaries">
            <h2>Beneficiaries</h2>
            <div className="beneficiaries-header-actions">
              <button type="button" className="btn btn-try-api" onClick={() => setShowTryApiSidebar(true)}>
                Try API
              </button>
            </div>
          </div>

          <div className="beneficiaries-filters">
            <div className="filter-group">
              <label>Beneficiary ID</label>
              <input
                type="text"
                className="form-input filter-input"
                placeholder="Filter by ID"
                value={filterBeneficiaryId}
                onChange={(e) => setFilterBeneficiaryId(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Account ID</label>
              <input
                type="text"
                className="form-input filter-input"
                placeholder="Filter by account"
                value={filterAccountId}
                onChange={(e) => setFilterAccountId(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Connection</label>
              <select
                className="form-input filter-input"
                value={filterPartnerId}
                onChange={(e) => setFilterPartnerId(e.target.value)}
              >
                <option value="">All connections</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Date range</label>
              <button
                type="button"
                className="form-input filter-input date-range-trigger"
                onClick={() => setShowDatePicker(true)}
              >
                {dateRangeLabel}
              </button>
            </div>
          </div>

          {showDatePicker && (
            <DateRangePicker
              startDate={dateFrom}
              endDate={dateTo}
              onDateChange={handleDateChange}
              onClose={() => setShowDatePicker(false)}
            />
          )}

          <table className="table">
            <thead>
              <tr>
                <th>Beneficiary ID</th>
                <th>Account ID</th>
                <th>Partner</th>
                <th>Individual / Business</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBeneficiaries.map((beneficiary) => (
                <tr key={beneficiary.id}>
                  <td><code className="beneficiary-id-cell">{beneficiary.id}</code></td>
                  <td>{beneficiary.accountId}</td>
                  <td>{beneficiary.partnerName}</td>
                  <td>{beneficiary.individualCount} / {beneficiary.businessCount}</td>
                  <td>{beneficiary.createdAt}</td>
                  <td>
                    <button type="button" className="btn btn-success btn-sm">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showTryApiSidebar && (
          <div className="try-api-sidebar-overlay" onClick={() => setShowTryApiSidebar(false)} aria-hidden="true" />
        )}
        {showTryApiSidebar && (
          <aside className="try-api-sidebar">
            <BeneficiaryApiExplorer
              baseUrl={apiBaseUrl}
              partners={partners}
              onClose={() => setShowTryApiSidebar(false)}
              onLogEntry={addLogForPartner}
            />
          </aside>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Beneficiaries;
